import { Check, Info, Loader2 } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { parseUnits } from 'viem';

import { Button } from '@/components/ui/button';
import { CopyableHash } from '@/components/ui/copyable-hash';
import {
    ANCHOR_CHAINS,
    ANCHOR_CHAIN_IDS,
    ANCHOR_WITHDRAW_TOKENS,
    DEFAULT_ANCHOR_CHAIN_ID,
    wolfswapSwapHref,
    type AnchorChainId,
    type AnchorStableToken,
} from '@/features/wallet/config/anchors';
import { SPEED_NETWORK_ID } from '@/lib/api/speed';
import type {
    BridgeCatalog,
    BridgeName,
    BridgeQuoteResponse,
} from '@/lib/api/speed';

import { useBridgeOverride } from '../bridgeOverride';
import { useBridgeQuote } from '../hooks/useBridgeQuote';
import type { DirectWithdrawState } from '../hooks/useDirectWithdraw';
import { phaseLabel } from '../operationPhase';
import { BRIDGE_LABELS } from '../services/bridgeFees';
import { defaultWithdrawDestination } from '../services/directWithdraw';
import type { WithdrawDestination } from '../services/directWithdraw';
import type { CustodialWithdrawState } from '../types';

import BridgeOverrideButton from './BridgeOverrideButton';
import OperationProgress from './OperationProgress';

/** Direct EOA route controls, provided by useDirectWithdraw (absent = feature not wired). */
export interface DirectWithdrawControls {
    state: DirectWithdrawState;
    run: () => Promise<void>;
    reset: () => void;
    isRunning: boolean;
    /** null while the HYPE gas read is in flight. */
    hypeGasOk: boolean | null;
}

interface CustodialWithdrawFormProps {
    withdrawState: CustodialWithdrawState;
    onWithdraw: (
        amount: bigint,
        destinationChainId: AnchorChainId,
        destinationCurrency?: string,
    ) => Promise<void>;
    onWithdrawAll: (
        destinationChainId: AnchorChainId,
        destinationCurrency?: string,
    ) => Promise<void>;
    onReset: () => void;
    onCancel: () => void;
    /**
     * The maximum this route may move, as the SERVER reports it (`withdrawableUsdc`). It is
     * deliberately not the wallet total: HyperCore dust below the withdraw floor and the
     * exit-sweep gas buffer are the user's money but no route can move them, and offering
     * them as a max hands the user a number the backend then refuses.
     */
    custodialBalance: number | null;
    /** Floor (USD) for the next withdrawal; hints hidden when absent (older backends). */
    minWithdrawUsd?: number | null;
    /** Estimated flat cost (USD) of the next withdrawal; hints hidden when absent. */
    flatFeeUsd?: number | null;
    isWithdrawing: boolean;
    tokenDecimals: number;
    hasActivePosition?: boolean;
    /** A Snappy Wallet setup is running; it spends from this wallet, so hold withdrawals. */
    isReactivating?: boolean;
    /**
     * The Snappy Wallet still has to be activated (its trading agent was never approved, or needs
     * re-approval). The bridged route only moves HyperCore funds out of an ACTIVATED wallet, so a
     * user in this state sees an empty bridged route while holding money; say why instead of
     * "no funds".
     */
    activationRequired?: boolean;
    /**
     * Total balance across all buckets (custodial + adapter + HyperCore). The DIRECT route can
     * move sub-floor funds the bridged route cannot, so its availability keys on this rather
     * than on the bridged `custodialBalance` cap.
     */
    totalBalance?: number | null;
    /** EOA-direct HyperEVM withdrawal; the destination picker is hidden when absent. */
    directWithdraw?: DirectWithdrawControls;
    /** A deposit (either route) is in flight — the same wallet signs both. */
    otherOperationRunning?: boolean;
    /**
     * The bridged destination the form PRESELECTS (typically the chain the
     * wallet is connected to). A preference only: the user picks any anchor
     * chain in the destination selector, per withdrawal.
     */
    anchorChainId?: AnchorChainId;
    /**
     * Display pre-quote for the entered amount. When it is supplied the button
     * stays shut until a successful quote for THAT amount is in hand, so the
     * user never commits to a price nobody has quoted. Absent (older wiring,
     * and the direct HyperEVM route, which does not bridge) means no gate and
     * the form behaves exactly as it did before.
     */
    /** The backend's bridge registry, for the testing switch's options; null while unknown. */
    bridgeCatalog?: BridgeCatalog | null;
    quoteWithdrawal?: (
        amount: bigint,
        destinationChainId: AnchorChainId,
        signal: AbortSignal,
        destinationCurrency?: string,
    ) => Promise<BridgeQuoteResponse>;
}

/**
 * USDC has six decimals, the panel shows two, and this particular number is a CAP. Rounding
 * it to the nearest cent can print a maximum a cent above the one the backend will honour,
 * which is the whole class of bug this figure exists to end. Always round down.
 */
function floorUsd(value: number): string {
    return (Math.floor(value * 100) / 100).toFixed(2);
}

/**
 * A display float turned into base units for the quote. Rounded to the token's
 * own precision first: a float that came back from `formatUnits` can carry
 * more decimals than the token has, and `parseUnits` refuses those outright.
 */
function safeBaseUnits(amount: number, decimals: number): bigint | null {
    try {
        return parseUnits(amount.toFixed(decimals), decimals);
    } catch {
        return null;
    }
}

function withEllipsis(label: string): string {
    const trimmed = label.trim();
    return trimmed.endsWith('...')
        ? trimmed
        : `${trimmed.replace(/\.+$/, '')}...`;
}

/** What the bridge's cut covers, per bridge: the tail of the (i) sentence. */
const BRIDGE_COST_DETAIL: Record<BridgeName, string> = {
    relay: ', including any swap impact into the payout token.',
    lifi: ", including LI.FI's own cut and the third-party bridge it routes through.",
    debridge: ', including its fixed network fee.',
};

/**
 * The small yellow (i) next to "You'll receive": on hover or focus it says where
 * the difference went. Two things can be in it, and each is named with its own
 * figure so the sum is checkable: the bridge's cost for THIS route (Relay and
 * deBridge charge very differently, so the label names the one quoted) and the
 * HyperCore reserve the sweep leaves behind, which is the user's money and not a
 * fee. Plain hover/focus reveal, no portal: the panel is a modal already.
 */
const ReceiveHint: React.FC<{
    bridge: BridgeName | null;
    bridgeCostUsd: number | null;
    reserveHeldUsd: number | null;
}> = ({ bridge, bridgeCostUsd, reserveHeldUsd }) => {
    const bridgeLabel = bridge ? BRIDGE_LABELS[bridge] : 'the bridge';
    const lines: string[] = [];
    if (bridgeCostUsd !== null) {
        lines.push(
            `About $${bridgeCostUsd.toFixed(2)} goes to ${bridgeLabel} for carrying the funds` +
                (bridge ? BRIDGE_COST_DETAIL[bridge] : '.'),
        );
    }
    if (reserveHeldUsd !== null && reserveHeldUsd >= 0.005) {
        lines.push(
            `$${reserveHeldUsd.toFixed(2)} stays in your HyperCore account. Hyperliquid charges a one time 1 USDC fee on an account's first outgoing transfer, and it removes the trading agent from an account that is emptied completely, so a small reserve is kept there. It is still your money.`,
        );
    }
    lines.push(
        'These are estimates. The exact amount is fixed when the bridge executes.',
    );
    return (
        <span className="group relative ml-1 inline-flex align-middle">
            <button
                type="button"
                aria-label="Why you receive less than you withdraw"
                className="inline-flex text-amber-400 hover:text-amber-300 focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400"
            >
                <Info size={11} aria-hidden="true" />
            </button>
            <span
                role="tooltip"
                className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 hidden w-64 -translate-x-1/2 rounded-md border border-amber-400/40 bg-popover px-2.5 py-2 text-left text-[10px] font-normal leading-relaxed text-popover-foreground shadow-lg group-hover:block group-focus-within:block"
            >
                {lines.map((line) => (
                    <span key={line} className="block [&+&]:mt-1.5">
                        {line}
                    </span>
                ))}
            </span>
        </span>
    );
};

const CustodialWithdrawForm: React.FC<CustodialWithdrawFormProps> = ({
    withdrawState,
    onWithdraw,
    onWithdrawAll,
    onReset,
    onCancel,
    custodialBalance,
    minWithdrawUsd,
    flatFeeUsd,
    isWithdrawing,
    tokenDecimals,
    hasActivePosition = false,
    isReactivating = false,
    activationRequired = false,
    totalBalance = null,
    directWithdraw,
    otherOperationRunning = false,
    anchorChainId,
    bridgeCatalog = null,
    quoteWithdrawal,
}) => {
    const [mode, setMode] = useState<'all' | 'custom'>('all');
    const [inputAmount, setInputAmount] = useState<string>('');
    // HyperEVM is preferred — direct (user-signed, no bridge, no service wallets) and cheaper —
    // but only for wallets that can pay its gas; see defaultWithdrawDestination. The bridged
    // route pays out on whichever anchor chain the user picks, preselecting the one their
    // wallet sits on.
    const [destination, setDestination] = useState<WithdrawDestination>(() =>
        defaultWithdrawDestination({
            directAvailable: !!directWithdraw,
            hypeGasOk: directWithdraw?.hypeGasOk ?? null,
            preferredAnchor: anchorChainId,
        }),
    );
    // once the user interacts with the form AT ALL, the arriving gas read must not move the
    // destination. picking a mode or typing an amount counts: flipping cronos -> hyperEvm under
    // a user mid-way through a custom bridged withdrawal would swap their partial amount for a
    // full direct sweep on the very button they were about to click.
    const [destinationTouched, setDestinationTouched] = useState(false);
    // the payout token on the bridged route: the anchor's default stable unless the user picked the
    // chain's other one (Cronos native USDC, when a bridge cannot deliver USDC.e directly)
    const [destinationCurrency, setDestinationCurrency] = useState<
        `0x${string}` | null
    >(null);

    const { status, error, txHash, progressMessage, progressStep } =
        withdrawState;

    const directAvailable = !!directWithdraw;
    const directGasOk = directWithdraw?.hypeGasOk ?? null;
    const anyOperationRunning =
        isWithdrawing || !!directWithdraw?.isRunning || otherOperationRunning;

    useEffect(() => {
        if (destinationTouched || anyOperationRunning) return;
        setDestination(
            defaultWithdrawDestination({
                directAvailable,
                hypeGasOk: directGasOk,
                preferredAnchor: anchorChainId,
            }),
        );
    }, [
        anchorChainId,
        anyOperationRunning,
        destinationTouched,
        directAvailable,
        directGasOk,
    ]);

    const numericAmount = parseFloat(inputAmount) || 0;
    const exceedsBalance =
        custodialBalance !== null && numericAmount > custodialBalance;
    // funds that are the user's but that this route cannot move (the HyperCore exit-sweep gas
    // buffer, plus core dust under the withdraw floor). Only worth mentioning once it is big
    // enough to see at the two decimals both figures are rendered with.
    const heldBack =
        custodialBalance !== null &&
        totalBalance !== null &&
        totalBalance - custodialBalance >= 0.005;
    const isValidCustom = mode === 'custom' && numericAmount > 0;
    const hasBalance = custodialBalance !== null && custodialBalance > 0;
    // min/net-receive hints only render when the backend reports the fields (older
    // backends omit them — behavior then stays exactly as before).
    const effectiveAmount =
        mode === 'all' ? (custodialBalance ?? 0) : numericAmount;

    // --- what this amount actually costs, asked before the button unlocks ---
    //
    // Only the BRIDGED route is priced: the direct HyperEVM withdrawal is the
    // user's own transaction straight into their wallet, with no bridge in it.
    // `destination === 'hyperEvm'` implies the direct controls are wired: the
    // default only picks it when they are, and the picker only offers it then.
    const bridgedRoute = destination !== 'hyperEvm';
    // the chain a bridged withdrawal pays out on; the 'hyperEvm' arm exists for
    // TypeScript narrowing only (see above), so its value never matters
    const bridgedDestination: AnchorChainId =
        destination === 'hyperEvm'
            ? (anchorChainId ?? DEFAULT_ANCHOR_CHAIN_ID)
            : destination;
    const quoteAmount6 =
        quoteWithdrawal &&
        bridgedRoute &&
        effectiveAmount > 0 &&
        !exceedsBalance
            ? safeBaseUnits(effectiveAmount, tokenDecimals)
            : null;
    // the destination is in the key because the payout chain decides the
    // bridge, and the bridge decides the fee and the floor.
    // the pinned bridge (testing switch) is in the key too: flipping it must re-price
    const bridgeOverride = useBridgeOverride((s) => s.bridge);
    const quoteKey =
        quoteAmount6 === null
            ? null
            : `${quoteAmount6}|${bridgedDestination}|${destinationCurrency ?? ''}|${bridgeOverride}`;
    const withdrawTokens = ANCHOR_WITHDRAW_TOKENS[bridgedDestination];
    const payoutToken =
        withdrawTokens.find(
            (token) =>
                token.address.toLowerCase() ===
                destinationCurrency?.toLowerCase(),
        ) ?? withdrawTokens[0];
    const runQuote = useCallback(
        async (signal: AbortSignal) =>
            quoteWithdrawal!(
                quoteAmount6!,
                bridgedDestination,
                signal,
                payoutToken.address,
            ),
        [
            bridgedDestination,
            payoutToken.address,
            quoteAmount6,
            quoteWithdrawal,
        ],
    );
    const priceQuote = useBridgeQuote({ key: quoteKey, fetch: runQuote });
    const quoted = priceQuote.quote;
    /** A price has to be in hand before this amount may be sent. */
    const quoteGates = quoteKey !== null;

    // The floor is per ROUTE, not per account: deBridge carries a higher one,
    // so the quote's figure replaces the balance endpoint's advertised one.
    const effectiveMinUsd =
        quoted?.minimumUsd ??
        (typeof minWithdrawUsd === 'number' ? minWithdrawUsd : null);
    const belowMinimum =
        effectiveMinUsd !== null &&
        effectiveAmount > 0 &&
        effectiveAmount < effectiveMinUsd;

    // Fee precedence, most-committed first. The withdrawal's own state wins
    // because by then the backend has re-quoted and the user has been shown (or
    // has signed for) that exact figure; the pre-quote is the estimate that got
    // them to the button; the flat estimate is the last resort.
    const effectiveFeeUsd =
        (typeof withdrawState.bridgeFeeUsd === 'number'
            ? withdrawState.bridgeFeeUsd
            : null) ??
        quoted?.bridgeFeeUsd ??
        (typeof flatFeeUsd === 'number' ? flatFeeUsd : null);
    const effectiveBridge = withdrawState.bridge ?? quoted?.bridge ?? null;
    // What lands. The quote's own landing figure is the truth for the amount it
    // priced (Relay nets its relayer fee, gas and swap impact; deBridge its fixed
    // fee and bps), so it beats "amount minus fee" whenever it is in hand for
    // exactly this amount. Once the withdrawal is running, its committed fee is
    // the only number left, so the subtraction takes over.
    const quotedReceive =
        typeof withdrawState.bridgeFeeUsd !== 'number' &&
        priceQuote.ready &&
        quoted?.ok &&
        quoted.estimatedReceive !== null &&
        quoted.estimatedReceive > 0
            ? quoted.estimatedReceive
            : null;
    const netReceive =
        quotedReceive ??
        (effectiveFeeUsd !== null && effectiveAmount > 0
            ? Math.max(0, effectiveAmount - effectiveFeeUsd)
            : null);
    // the amount the bridge keeps, as shown next to "you'll receive"
    const bridgeCostUsd =
        netReceive !== null && effectiveAmount > 0
            ? Math.max(0, effectiveAmount - netReceive)
            : effectiveFeeUsd;
    const reserveHeldUsd =
        heldBack && custodialBalance !== null && totalBalance !== null
            ? totalBalance - custodialBalance
            : null;
    // the bridge's cut, named, next to the figure it explains. Shown for BOTH
    // bridges (Relay's is small, deBridge's is not) so the box reads the same
    // whichever one carries the money.
    const bridgeFeeLine =
        bridgeCostUsd !== null && effectiveAmount > 0
            ? `${effectiveBridge ? BRIDGE_LABELS[effectiveBridge] : 'Bridge'} fee ≈ $${bridgeCostUsd.toFixed(2)}`
            : null;
    // A disabled button with nothing next to it reads as broken, so say which
    // of the two it is: still pricing, or priced and refused.
    // the chain's other stable, when the quoted bridge cannot deliver the picked one directly: the
    // backend's sentence already says to withdraw it and swap on WolfSwap; the form adds the switch
    // and the pre-filled swap link (the deposit form's precedent for bridgeTokenUnsupported)
    const alternateToken =
        priceQuote.reasonCode === 'bridgeTokenUnsupported'
            ? (withdrawTokens.find(
                  (token) => token.address !== payoutToken.address,
              ) ?? null)
            : null;
    const quoteNotice: {
        text: string;
        blocking: boolean;
        link?: { href: string; label: string };
        switchTo?: AnchorStableToken;
    } | null = !quoteGates
        ? null
        : priceQuote.reason
          ? {
                text: priceQuote.reason,
                blocking: true,
                ...(alternateToken
                    ? {
                          link: {
                              href: wolfswapSwapHref(
                                  bridgedDestination,
                                  alternateToken.address,
                                  payoutToken.address,
                              ),
                              label: `Swap ${alternateToken.symbol} to ${payoutToken.symbol} on WolfSwap`,
                          },
                          switchTo: alternateToken,
                      }
                    : {}),
            }
          : priceQuote.ready
            ? null
            : { text: 'Checking the current bridge fee...', blocking: false };

    // hard clamp, so the typed amount can never exceed what the backend will move. Applied on
    // blur rather than per keystroke: rewriting the field mid-type fights the user.
    const handleAmountBlur = () => {
        if (custodialBalance === null || !inputAmount) return;
        if (parseFloat(inputAmount) > custodialBalance) {
            // full precision, not a rounded display value: rounding UP would land right
            // back above the cap it is clamping to.
            setInputAmount(String(custodialBalance));
        }
    };

    const handleWithdraw = () => {
        if (mode === 'all') {
            void onWithdrawAll(bridgedDestination, payoutToken.address);
        } else if (isValidCustom && !exceedsBalance) {
            const amountBigInt = parseUnits(inputAmount, tokenDecimals);
            void onWithdraw(
                amountBigInt,
                bridgedDestination,
                payoutToken.address,
            );
        }
    };

    // the backend labels four different HyperEVM steps "Preparing withdrawal"; name whichever one
    // this actually is, and fall back to its own message for anything unmapped.
    const phase = phaseLabel(progressStep, 'withdraw');
    const processingLabel = withEllipsis(
        phase?.label ??
            progressMessage ??
            (status === 'submitting'
                ? 'Starting withdrawal...'
                : 'Bridging funds to your wallet...'),
    );

    if (status === 'success') {
        return (
            <div className="flex flex-col items-center gap-4 py-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-success/30 bg-success/10">
                    <span className="text-2xl text-success" aria-hidden="true">
                        ✓
                    </span>
                </div>
                <div className="text-center">
                    <p
                        role="status"
                        className="text-sm font-semibold text-success"
                    >
                        Withdrawal confirmed
                    </p>
                    {txHash && <CopyableHash value={txHash} className="mt-2" />}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={onReset}
                    className="mt-2"
                >
                    Done
                </Button>
            </div>
        );
    }

    if (status === 'failed') {
        return (
            <div className="flex flex-col items-center gap-4 py-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-destructive/30 bg-destructive/10">
                    <span
                        className="text-2xl text-destructive"
                        aria-hidden="true"
                    >
                        ✗
                    </span>
                </div>
                <div className="text-center">
                    <p
                        role="alert"
                        className="text-sm font-semibold text-destructive"
                    >
                        Withdrawal failed
                    </p>
                    {error && (
                        <p className="mt-1 max-w-[280px] text-[10px] text-muted-foreground">
                            {error}
                        </p>
                    )}
                </div>
                {/* a rejected exit sweep leaves the funds untouched, so trying again is the
                    whole remedy — send the user back to the form rather than out of the panel. */}
                <div className="flex w-full gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onCancel}
                        className="flex-1"
                    >
                        Dismiss
                    </Button>
                    <Button size="sm" onClick={onReset} className="flex-1">
                        Retry
                    </Button>
                </div>
            </div>
        );
    }

    if (directWithdraw?.state.status === 'success') {
        return (
            <div className="flex flex-col items-center gap-4 py-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-success/30 bg-success/10">
                    <span className="text-2xl text-success" aria-hidden="true">
                        ✓
                    </span>
                </div>
                <div className="text-center">
                    <p
                        role="status"
                        className="text-sm font-semibold text-success"
                    >
                        Withdrawal confirmed
                    </p>
                    <p className="mt-1 text-[10px] text-muted-foreground">
                        {directWithdraw.state.deliveredUsd !== null
                            ? `$${directWithdraw.state.deliveredUsd.toFixed(2)} sent to your wallet on HyperEVM`
                            : 'Funds sent to your wallet on HyperEVM'}
                    </p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={directWithdraw.reset}
                    className="mt-2"
                >
                    Done
                </Button>
            </div>
        );
    }

    if (directWithdraw?.state.status === 'failed') {
        return (
            <div className="flex flex-col items-center gap-4 py-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-destructive/30 bg-destructive/10">
                    <span
                        className="text-2xl text-destructive"
                        aria-hidden="true"
                    >
                        ✗
                    </span>
                </div>
                <div className="text-center">
                    <p
                        role="alert"
                        className="text-sm font-semibold text-destructive"
                    >
                        Withdrawal failed
                    </p>
                    {directWithdraw.state.error && (
                        <p className="mt-1 max-w-[280px] text-[10px] text-muted-foreground">
                            {directWithdraw.state.error}
                        </p>
                    )}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={directWithdraw.reset}
                >
                    Dismiss
                </Button>
            </div>
        );
    }

    if (hasActivePosition) {
        return (
            <div className="flex flex-col items-center gap-3 py-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-muted-foreground/20 bg-muted/30">
                    <span className="text-xl text-muted-foreground">⊘</span>
                </div>
                <p className="text-center font-sans text-xs text-muted-foreground">
                    Close your open positions before withdrawing funds.
                </p>
            </div>
        );
    }

    // the direct route can move balances the bridged route cannot (sub-floor HyperCore funds),
    // so "nothing to withdraw" is only true when BOTH routes have nothing.
    const hasDirectFunds = !!directWithdraw && (totalBalance ?? 0) > 0;
    // money the user holds that the bridged route will not move until the wallet is activated:
    // the reason for the empty bridged route is the activation, and the copy has to say so.
    const heldPendingActivation = activationRequired && (totalBalance ?? 0) > 0;
    if (!hasBalance && !hasDirectFunds) {
        return (
            <div className="flex flex-col items-center gap-3 py-6">
                <p className="text-center text-xs text-muted-foreground">
                    {heldPendingActivation
                        ? 'Activate your Snappy Wallet to withdraw these funds to Cronos or Robinhood.'
                        : 'No funds available to withdraw.'}
                </p>
            </div>
        );
    }

    const direct = directWithdraw;
    const showDirect = destination === 'hyperEvm' && !!direct;
    const anyRouteBusy = anyOperationRunning;

    return (
        <div className="flex flex-col gap-4">
            <div>
                <label
                    htmlFor="withdraw-destination"
                    className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                >
                    Destination
                </label>
                <select
                    id="withdraw-destination"
                    value={
                        destination === 'hyperEvm'
                            ? 'hyperEvm'
                            : `${destination}:${payoutToken.address}`
                    }
                    onChange={(event) => {
                        setDestinationTouched(true);
                        const next = event.target.value;
                        if (next === 'hyperEvm') {
                            setDestination('hyperEvm');
                            setDestinationCurrency(null);
                            return;
                        }
                        const [chain, token] = next.split(':');
                        setDestination(Number(chain) as AnchorChainId);
                        setDestinationCurrency(
                            (token as `0x${string}`) ?? null,
                        );
                    }}
                    disabled={anyRouteBusy}
                    className="h-10 w-full rounded-lg border border-muted-foreground/70 bg-background px-3 text-sm text-foreground focus:border-primary focus:outline-none disabled:opacity-50"
                >
                    {direct && (
                        <option value="hyperEvm">
                            HyperEVM (direct to your wallet)
                        </option>
                    )}
                    {ANCHOR_CHAIN_IDS.flatMap((chainId) =>
                        ANCHOR_WITHDRAW_TOKENS[chainId].map((token) => (
                            <option
                                key={`${chainId}:${token.address}`}
                                value={`${chainId}:${token.address}`}
                            >
                                {`${ANCHOR_CHAINS[chainId].label} (bridged, ${token.symbol})`}
                            </option>
                        )),
                    )}
                </select>
            </div>

            {otherOperationRunning && (
                <p className="text-[11px] leading-relaxed text-amber-400">
                    A deposit is in progress, you can withdraw again once it
                    finishes.
                </p>
            )}

            {showDirect ? (
                <>
                    {direct.isRunning ? (
                        <div
                            role="status"
                            className="rounded-lg border border-border bg-secondary/40 px-3 py-2"
                        >
                            <div className="flex items-center gap-2">
                                <Loader2
                                    size={14}
                                    className="animate-spin text-muted-foreground"
                                />
                                <span className="text-xs text-muted-foreground">
                                    {withEllipsis(
                                        direct.state.stepLabel ??
                                            'Preparing withdrawal',
                                    )}
                                </span>
                                {direct.state.stepCount > 0 && (
                                    <span className="ml-auto shrink-0 text-[10px] tabular-nums text-muted-foreground/70">
                                        {`Step ${direct.state.stepIndex} of ${direct.state.stepCount}`}
                                    </span>
                                )}
                            </div>
                            <p className="mt-1 text-[10px] leading-relaxed text-muted-foreground/70">
                                Confirm each transaction in your wallet, every
                                step is signed by you.
                            </p>
                        </div>
                    ) : (
                        <div className="rounded-lg border border-border bg-card/50 px-3 py-2">
                            <p className="text-xs text-muted-foreground">
                                Withdraws your full balance
                                {totalBalance !== null && (
                                    <>
                                        {' '}
                                        <span className="font-mono font-semibold tabular-nums text-foreground">
                                            ${totalBalance.toFixed(2)}
                                        </span>
                                    </>
                                )}{' '}
                                directly to your connected wallet on HyperEVM.
                            </p>
                            <p className="mt-1 text-[10px] leading-relaxed text-muted-foreground">
                                You sign every transaction yourself, no bridge,
                                no minimum, only network gas.
                            </p>
                        </div>
                    )}

                    {direct.hypeGasOk === false && !direct.isRunning && (
                        <p
                            role="alert"
                            className="text-[11px] leading-relaxed text-amber-400"
                        >
                            Your wallet needs a small amount of HYPE on HyperEVM
                            to pay gas for the direct withdrawal.
                        </p>
                    )}

                    {isReactivating && (
                        <p className="mb-2 text-[11px] leading-relaxed text-amber-400">
                            Snappy Wallet setup is running, you can withdraw
                            again once it finishes.
                        </p>
                    )}

                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onCancel}
                            disabled={direct.isRunning}
                            className="flex-1"
                        >
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            onClick={() => void direct.run()}
                            disabled={
                                anyRouteBusy ||
                                isReactivating ||
                                !hasDirectFunds ||
                                direct.hypeGasOk === false
                            }
                            className="flex-1"
                        >
                            {direct.isRunning ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                'Withdraw'
                            )}
                        </Button>
                    </div>
                </>
            ) : !hasBalance ? (
                // bridged route selected but the saga has nothing it can move (e.g. only
                // sub-floor HyperCore dust, which the direct route CAN take)
                <div className="flex flex-col items-center gap-3 py-6">
                    <p className="text-center text-xs text-muted-foreground">
                        {heldPendingActivation
                            ? 'Activate your Snappy Wallet to withdraw to Cronos or Robinhood. Until then, pick HyperEVM to withdraw directly to your connected wallet.'
                            : 'No funds the bridged route can move, the HyperEVM destination can withdraw them directly.'}
                    </p>
                </div>
            ) : (
                <>
                    <div>
                        <label
                            id="withdraw-mode-label"
                            className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                        >
                            Withdraw mode
                        </label>
                        <div
                            role="group"
                            aria-labelledby="withdraw-mode-label"
                            className="grid grid-cols-2 gap-2"
                        >
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setDestinationTouched(true);
                                    setMode('all');
                                }}
                                aria-pressed={mode === 'all'}
                                className={
                                    mode === 'all'
                                        ? 'gap-1.5 border-primary/50 bg-primary/10 text-primary'
                                        : ''
                                }
                            >
                                {mode === 'all' && <Check size={12} />}
                                Withdraw all
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setDestinationTouched(true);
                                    setMode('custom');
                                }}
                                aria-pressed={mode === 'custom'}
                                className={
                                    mode === 'custom'
                                        ? 'gap-1.5 border-primary/50 bg-primary/10 text-primary'
                                        : ''
                                }
                            >
                                {mode === 'custom' && <Check size={12} />}
                                Custom
                            </Button>
                        </div>
                    </div>

                    {/* the wallet total and the withdrawable max are different numbers, and a
                        user who sees only the total has no way to find out why the bigger one
                        is refused. Say it once, in the panel that offers the max. */}
                    {heldBack && (
                        <p className="text-[10px] leading-relaxed text-muted-foreground">
                            A small reserve stays in your trading account for
                            HyperCore's one-time first transfer fee and transfer
                            gas.
                        </p>
                    )}

                    {mode === 'custom' && (
                        <div>
                            <label
                                htmlFor="withdraw-amount"
                                className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                            >
                                Amount (USDC)
                            </label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm text-muted-foreground">
                                    $
                                </span>
                                <input
                                    id="withdraw-amount"
                                    type="number"
                                    value={inputAmount}
                                    onChange={(e) => {
                                        setDestinationTouched(true);
                                        setInputAmount(e.target.value);
                                    }}
                                    onBlur={handleAmountBlur}
                                    placeholder="0.00"
                                    min={0}
                                    max={custodialBalance ?? undefined}
                                    disabled={isWithdrawing}
                                    aria-invalid={
                                        exceedsBalance ||
                                        belowMinimum ||
                                        undefined
                                    }
                                    aria-describedby={
                                        exceedsBalance || belowMinimum
                                            ? 'withdraw-amount-error'
                                            : undefined
                                    }
                                    className="h-10 w-full rounded-lg border border-muted-foreground/70 bg-background pl-6 pr-3 text-right font-mono font-bold tabular-nums text-foreground focus:border-primary focus:outline-none disabled:opacity-50"
                                />
                            </div>
                            {effectiveMinUsd !== null && (
                                <p className="mt-1 text-[10px] text-muted-foreground">
                                    {`Min $${effectiveMinUsd.toFixed(2)}`}
                                    {custodialBalance !== null &&
                                        ` · Max $${floorUsd(custodialBalance)}`}
                                    {netReceive !== null &&
                                        !exceedsBalance &&
                                        !belowMinimum && (
                                            <>
                                                {` · You'll receive ≈ $${netReceive.toFixed(2)}`}
                                                <ReceiveHint
                                                    bridge={effectiveBridge}
                                                    bridgeCostUsd={
                                                        bridgeCostUsd
                                                    }
                                                    reserveHeldUsd={
                                                        reserveHeldUsd
                                                    }
                                                />
                                                {bridgeFeeLine && (
                                                    <span className="block">
                                                        {bridgeFeeLine}
                                                    </span>
                                                )}
                                            </>
                                        )}
                                </p>
                            )}
                            {exceedsBalance ? (
                                <p
                                    id="withdraw-amount-error"
                                    role="alert"
                                    className="mt-1 text-[10px] text-destructive"
                                >
                                    Exceeds the amount available to withdraw
                                </p>
                            ) : (
                                belowMinimum && (
                                    <p
                                        id="withdraw-amount-error"
                                        role="alert"
                                        className="mt-1 text-[10px] text-destructive"
                                    >
                                        {`Minimum withdrawal is $${effectiveMinUsd!.toFixed(2)}`}
                                    </p>
                                )
                            )}
                        </div>
                    )}

                    {mode === 'all' && custodialBalance !== null && (
                        <div className="rounded-lg border border-border bg-card/50 px-3 py-2">
                            <p className="text-xs text-muted-foreground">
                                Available to withdraw:{' '}
                                <span className="font-mono font-semibold tabular-nums text-foreground">
                                    ${floorUsd(custodialBalance)}
                                </span>
                            </p>
                            {netReceive !== null && !belowMinimum && (
                                <p className="mt-1 text-[10px] text-muted-foreground">
                                    {`You'll receive ≈ $${netReceive.toFixed(2)}`}
                                    <ReceiveHint
                                        bridge={effectiveBridge}
                                        bridgeCostUsd={bridgeCostUsd}
                                        reserveHeldUsd={reserveHeldUsd}
                                    />
                                    {bridgeFeeLine && (
                                        <span className="block">
                                            {bridgeFeeLine}
                                        </span>
                                    )}
                                </p>
                            )}
                            {belowMinimum && (
                                <p
                                    role="alert"
                                    className="mt-1 text-[10px] text-destructive"
                                >
                                    {`Minimum withdrawal is $${effectiveMinUsd!.toFixed(2)}`}
                                </p>
                            )}
                        </div>
                    )}

                    {quoteNotice && !belowMinimum && !exceedsBalance && (
                        <p
                            role={quoteNotice.blocking ? 'alert' : 'status'}
                            className={
                                quoteNotice.blocking
                                    ? 'text-[10px] text-destructive'
                                    : 'text-[10px] text-muted-foreground'
                            }
                        >
                            {quoteNotice.text}
                            {quoteNotice.switchTo && (
                                <>
                                    {' '}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setDestinationTouched(true);
                                            setDestinationCurrency(
                                                quoteNotice.switchTo!.address,
                                            );
                                        }}
                                        className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80"
                                    >
                                        {`Withdraw ${quoteNotice.switchTo.symbol} instead`}
                                    </button>
                                </>
                            )}
                            {quoteNotice.link && (
                                <>
                                    {' '}
                                    <a
                                        href={quoteNotice.link.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80"
                                    >
                                        {quoteNotice.link.label}
                                    </a>
                                </>
                            )}
                        </p>
                    )}

                    {isWithdrawing && (
                        <div
                            role="status"
                            className="rounded-lg border border-border bg-secondary/40 px-3 py-2"
                        >
                            <div className="flex items-center gap-2">
                                <Loader2
                                    size={14}
                                    className="animate-spin text-muted-foreground"
                                />
                                <span className="text-xs text-muted-foreground">
                                    {processingLabel}
                                </span>
                                {phase?.index !== undefined && (
                                    // four consecutive bars restarting reads as a stuck loop without this
                                    <span className="ml-auto shrink-0 text-[10px] tabular-nums text-muted-foreground/70">
                                        {`Step ${phase.index} of ${phase.total}`}
                                    </span>
                                )}
                            </div>
                            {/* time-based, per phase. The backend's own `progress` is a coarse saga
                        percentage that sits still for a minute at a time, because each step is
                        waiting on a block rather than making measurable headway. */}
                            <OperationProgress
                                direction="withdraw"
                                step={progressStep}
                                className="mt-2"
                            />
                        </div>
                    )}

                    {isReactivating && (
                        <p className="mb-2 text-[11px] leading-relaxed text-amber-400">
                            Snappy Wallet setup is running, you can withdraw
                            again once it finishes.
                        </p>
                    )}

                    <div className="flex items-center gap-2">
                        {bridgedRoute && (
                            <BridgeOverrideButton
                                disabled={isWithdrawing}
                                catalog={bridgeCatalog}
                                originChainId={SPEED_NETWORK_ID}
                                destinationChainId={bridgedDestination}
                                leg="withdraw"
                            />
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onCancel}
                            disabled={isWithdrawing}
                            className="flex-1"
                        >
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleWithdraw}
                            disabled={
                                isWithdrawing ||
                                otherOperationRunning ||
                                isReactivating ||
                                belowMinimum ||
                                // no price in hand for THIS amount, no send
                                (quoteGates && !priceQuote.ready) ||
                                (mode === 'custom' &&
                                    (!isValidCustom || exceedsBalance))
                            }
                            className="flex-1"
                        >
                            {isWithdrawing ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                'Withdraw'
                            )}
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

export default CustodialWithdrawForm;
